import {
  APP_INITIALIZER,
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import {
  NavigationEnd,
  NavigationError,
  provideRouter,
  Router,
  withComponentInputBinding,
} from '@angular/router';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { registerLocaleData } from '@angular/common';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import UrbanoPreset from './core/theme/urbano.preset';
import localeEs from '@angular/common/locales/es';

registerLocaleData(localeEs);

export function initializePrimeNG(primeng: PrimeNG) {
  return () => {
    primeng.setTranslation({
      firstDayOfWeek: 1,
      dayNames: [
        'Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'
      ],
      dayNamesShort: [
        'Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'
      ],
      dayNamesMin: [
        'D', 'L', 'M', 'X', 'J', 'V', 'S'
      ],
      monthNames: [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
      ],
      monthNamesShort: [
        'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
        'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
      ],
      today: 'Hoy',
      clear: 'Limpiar',
      apply: 'Aplicar',
    });
  };
}

/**
 * Después de un deploy, S3 borra los bundles hasheados antiguos. Si un usuario ya
 * tenía la SPA abierta desde antes de ese deploy y navega (sin haber hecho un
 * refresh normal de por medio) a una ruta lazy-loaded cuyo chunk cambió, el
 * navegador pide un archivo que ya no existe (404) y Angular lo reporta como
 * NavigationError.
 *
 * La app no tiene guards `canDeactivate`, así que el usuario ya perdía cualquier
 * cambio sin guardar de la pantalla actual en el momento en que decidió navegar
 * (eso pasa igual sin este bug). Por eso no hace falta avisar: simplemente
 * completamos esa navegación con una recarga dura hacia la URL destino.
 */
export function setupChunkErrorReload(router: Router) {
  return () => {
    const RELOAD_FLAG = 'tms_chunk_reload_attempted';
    router.events.subscribe((event) => {
      if (event instanceof NavigationError) {
        const message = event.error?.message ?? '';
        const isChunkLoadError =
          /Failed to fetch dynamically imported module|Loading chunk .* failed|ChunkLoadError|error loading dynamically imported module/i.test(
            message
          );
        if (isChunkLoadError && !sessionStorage.getItem(RELOAD_FLAG)) {
          sessionStorage.setItem(RELOAD_FLAG, '1');
          window.location.href = event.url;
        }
      } else if (event instanceof NavigationEnd) {
        // Navegación exitosa: si el reload ya trajo el bundle correcto, liberamos la
        // bandera para que un deploy futuro dentro de la misma pestaña pueda volver
        // a activar este mecanismo.
        sessionStorage.removeItem(RELOAD_FLAG);
      }
    });
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    MessageService,
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor])),
    provideAnimationsAsync(),
    {
      provide: APP_INITIALIZER,
      useFactory: setupChunkErrorReload,
      deps: [Router],
      multi: true,
    },
    providePrimeNG({
      theme: {
        preset: UrbanoPreset,
        options: {
          prefix:           'p',
          darkModeSelector: '[data-theme="dark"]',
          cssLayer:         false,
        },
      },
      zIndex: {
        modal:   1100,
        overlay: 1200,
        menu:    1200,
        tooltip: 1300,
      },
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializePrimeNG,
      deps: [PrimeNG],
      multi: true,
    },
  ],
};
